import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PasswordIcon from "@mui/icons-material/Password";
import LoginIcon from "@mui/icons-material/Login";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import BusinessIcon from "@mui/icons-material/Business";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import React, { useContext, useMemo } from "react";
import { useTextDomainContext } from "@/app/providers";

interface MenuItemUserProps<TRow extends object = Record<string, unknown>> {
  row?: TRow;
  menuFor?: string;
  handleClose: () => void;
  menuClick: (_action: string, _row?: TRow) => void;
  showItems?: {
    showEdit?: boolean;
    showProfile?: boolean;
    showChangePassword?: boolean;
    showSystemRoles?: boolean;
    showCompanies?: boolean;
    showLogin?: boolean;
    showUnlink?: boolean;
    showDelete?: boolean;
    showSettings?: boolean;
    showNotifications?: boolean;
    [key: string]: boolean | undefined;
  };
}
export const MenuItemUser = <TRow extends object = Record<string, unknown>>(
  props: MenuItemUserProps<TRow>,
): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const menuFor = useMemo(() => {
    return props.menuFor ?? "";
  }, [props.menuFor]);

  const handleClose = () => {
    props.handleClose();
  };

  const handleEditAction = () => {
    props.menuClick(`${menuFor}-edit`, props.row);
    handleClose();
  };

  const handleProfileAction = () => {
    props.menuClick(`${menuFor}-profile`, props.row);
    handleClose();
  };

  const handleSettingsAction = () => {
    props.menuClick(`${menuFor}-settings`, props.row);
    handleClose();
  };

  const handleNotificationsAction = () => {
    props.menuClick(`${menuFor}-notifications`, props.row);
    handleClose();
  };

  const handlePasswordAction = () => {
    props.menuClick(`${menuFor}-password`, props.row);
    handleClose();
  };

  const handleCompanyAction = () => {
    props.menuClick(`${menuFor}-company`, props.row);
    handleClose();
  };

  const handleSystemRoleAction = () => {
    props.menuClick(`${menuFor}-system-role`, props.row);
    handleClose();
  };

  const handleLoginAction = () => {
    props.menuClick(`${menuFor}-login`, props.row);
    handleClose();
  };

  const handleUnlinkAction = () => {
    props.menuClick(`${menuFor}-unlink`, props.row);
    handleClose();
  };

  const handleDeleteAction = () => {
    props.menuClick(`${menuFor}-delete`, props.row);
    handleClose();
  };

  return (
    <>
      {props?.showItems?.showEdit && (
        <MenuItem onClick={handleEditAction}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Edit")} />
        </MenuItem>
      )}

      {props?.showItems?.showProfile && (
        <MenuItem onClick={handleProfileAction}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Profile")} />
        </MenuItem>
      )}

      {props?.showItems?.showSettings && (
        <MenuItem onClick={handleSettingsAction}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Settings")} />
        </MenuItem>
      )}

      {props?.showItems?.showNotifications && (
        <MenuItem onClick={handleNotificationsAction}>
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Notifications")} />
        </MenuItem>
      )}

      {props?.showItems?.showChangePassword && (
        <MenuItem onClick={handlePasswordAction}>
          <ListItemIcon>
            <PasswordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Change Password")} />
        </MenuItem>
      )}

      {props?.showItems?.showSystemRoles && (
        <MenuItem onClick={handleSystemRoleAction}>
          <ListItemIcon>
            <LockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("System Roles")} />
        </MenuItem>
      )}

      {props?.showItems?.showCompanies && (
        <MenuItem onClick={handleCompanyAction}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Companies")} />
        </MenuItem>
      )}

      {props?.showItems?.showLogin && (
        <MenuItem onClick={handleLoginAction}>
          <ListItemIcon>
            <LoginIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={gettext("Login")} />
        </MenuItem>
      )}

      {props?.showItems?.showUnlink && (
        <MenuItem onClick={handleUnlinkAction}>
          <ListItemIcon>
            <DoDisturbOnIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={gettext("Unlink")}
            slotProps={{
              primary: {
                color: "error",
              },
            }}
          />
        </MenuItem>
      )}

      {props?.showItems?.showDelete && (
        <MenuItem disabled onClick={handleDeleteAction}>
          <ListItemIcon>
            <DeleteIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={gettext("Delete")}
            slotProps={{
              primary: {
                color: "error",
              },
            }}
          />
        </MenuItem>
      )}
    </>
  );
};
